import React, { createContext, useContext, ReactNode } from 'react';
import { useWhiteLabelBranding } from '../hooks/useWhiteLabelBranding';

interface WhiteLabelData {
  isWhiteLabel: boolean;
  domain?: string;
  tenant?: {
    id: string;
    companyName: string;
    tagline?: string;
    contactEmail?: string;
    contactPhone?: string;
    licenseNumber?: string;
    licenseState?: string;
    description?: string;
  };
  branding?: {
    primaryColor: string;
    secondaryColor: string;
    logoUrl?: string;
    faviconUrl?: string;
    fontFamily: string;
    customCss?: string;
  };
  contact?: {
    phone: string;
    tollFree: string;
    email: string;
    supportEmail: string;
    privacyEmail: string;
    legalEmail: string;
    fairhousingEmail: string;
  };
  address?: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  hero?: {
    title: string;
    subtitle: string;
    showAiBadge: boolean;
    backgroundImage?: string;
  };
  footer?: {
    copyrightText: string;
    showPolicies: boolean;
    showFairHousing: boolean;
    showMlsDisclaimer: boolean;
  };
  features?: {
    customDomain: string;
    whiteLabel: boolean;
    hideKairosIQBranding: boolean;
    showAiChat: boolean;
    showPropertySearch: boolean;
    showHomeValuation: boolean;
  };
}

interface WhiteLabelContextType extends WhiteLabelData {
  isLoading: boolean;
  error: string | null;
}

const WhiteLabelContext = createContext<WhiteLabelContextType>({
  isWhiteLabel: false,
  isLoading: true,
  error: null
});

interface WhiteLabelProviderProps {
  children: ReactNode;
}

export const WhiteLabelProvider: React.FC<WhiteLabelProviderProps> = ({ children }) => {
  const whiteLabelData = useWhiteLabelBranding();

  return (
    <WhiteLabelContext.Provider value={whiteLabelData}>
      {children}
    </WhiteLabelContext.Provider>
  );
};

export const useWhiteLabel = () => {
  const context = useContext(WhiteLabelContext);
  if (!context) {
    throw new Error('useWhiteLabel must be used within a WhiteLabelProvider');
  }
  return context;
};

// Helper functions for common white-label values with fallbacks
export const getCompanyName = (whiteLabel: WhiteLabelContextType) => {
  return whiteLabel.isWhiteLabel && whiteLabel.tenant 
    ? whiteLabel.tenant.companyName 
    : 'KairosIQ AI Realty';
};

export const getContactEmail = (whiteLabel: WhiteLabelContextType, type: 'support' | 'privacy' | 'legal' | 'fairhousing' = 'support') => {
  if (!whiteLabel.isWhiteLabel || !whiteLabel.contact) {
    const defaultEmails = {
      support: 'michael@kairosiq.ai',
      privacy: 'privacy@kairosiq.ai',
      legal: 'legal@kairosiq.ai',
      fairhousing: 'fairhousing@kairosiq.ai'
    };
    return defaultEmails[type];
  }
  
  const emails = {
    support: whiteLabel.contact.supportEmail,
    privacy: whiteLabel.contact.privacyEmail,
    legal: whiteLabel.contact.legalEmail,
    fairhousing: whiteLabel.contact.fairhousingEmail
  };
  
  return emails[type];
};

export const getAddress = (whiteLabel: WhiteLabelContextType) => {
  if (!whiteLabel.isWhiteLabel || !whiteLabel.address) {
    return {
      street: '2700 S Gilbert Rd, Suite 5',
      city: 'Chandler',
      state: 'AZ',
      zip: '85286'
    };
  }
  return whiteLabel.address;
};

export const getLicenseInfo = (whiteLabel: WhiteLabelContextType) => {
  if (!whiteLabel.isWhiteLabel || !whiteLabel.tenant) {
    return 'KairosIQ AI Realty · License #CO721005000';
  }
  return `${whiteLabel.tenant.companyName} · License #${whiteLabel.tenant.licenseNumber}`;
};